'use client';

import { useFeatureFlag } from '@/@feature-flags/hooks';
import { BaseComponentProps } from '@/@studio-ai/props';
import { ConfirmationModal } from '@/components/ui/organisms/ConfirmationModal';
import { RenameModal } from '@/components/ui/organisms/RenameModal';
import { Button, Chip, Input, ScrollShadow, Tab, Tabs } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NEW_CONVERSATION_ID, STUDIO_AI_NAME } from '../../constants';
import { isPptxConversation, isPptxReport } from '../../hooks/useActivePlannerReports';
import { useStudioAI } from '../../context';
import type { SharedWithMeConversation, StudioAIFolder, StudioAIReport } from '../../types';
import { ConversationsTab } from './conversations-tab';
import { FeedbackTab } from './feedback-tab';
import { ReportsTab } from './reports-tab';
import { ShareModal } from './share-modal';
import { SharedTab } from './shared-tab';

type ActiveTab = 'conversations' | 'reports' | 'shared' | 'feedback';

const VALID_TABS: ActiveTab[] = ['conversations', 'reports', 'shared', 'feedback'];

export function HistoryManagement({ className = '' }: BaseComponentProps) {
    const {
        currentConversationId,
        conversations,
        conversationsLoading,
        deleteConversation,
        renameConversation,
        openConversation,
        startNewConversation,
        duplicateConversation,
        folders,
        foldersLoading,
        createFolder,
        renameFolder,
        deleteFolder,
        moveConversationToFolder,
        sharedWithMe,
        sharedWithMeLoading,
        organizationUsers,
        shareConversation,
        getConversationShares,
        revokeShare,
        searchConversations,
        conversationSearchTerm,
        conversationPage,
        conversationLimit,
        totalUnfiledConversations,
        totalPages,
        setConversationPage,
        setConversationLimit,
        userFeedback,
        userFeedbackLoading,
        userFeedbackFilter,
        setUserFeedbackFilter,
        markFeedbackAsRead,
        refetchUserFeedback,
        unreadFeedbackCount,
        reports,
        reportsLoading,
        folderedConversations,
        folderedReports,
        folderConversationTotals,
        folderReportTotals,
        folderConversationParams,
        folderReportParams,
        setFolderConversationPage,
        setFolderConversationLimit,
        setFolderReportPage,
        setFolderReportLimit,
        conversationsIsFetching,
        reportsIsFetching,
        folderConversationFetching,
        folderReportFetching,
        searchReports,
        reportSearchTerm,
        reportPage,
        reportLimit,
        totalUnfiledReports,
        reportTotalPages,
        setReportPage,
        setReportLimit,
    } = useStudioAI();

    const isPlannerEnabled = useFeatureFlag('studio-ai-planner-mode');
    const router = useRouter();
    const searchParams = useSearchParams();

    const tabParam = searchParams.get('tab') as ActiveTab | null;
    const initialTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'conversations';

    const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
    const [localSearch, setLocalSearch] = useState(conversationSearchTerm);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (tabParam && VALID_TABS.includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [tabParam]);

    const handleTabChange = useCallback((key: React.Key | null) => {
        if (key == null) return;
        const tab = key as ActiveTab;
        setActiveTab(tab);
        const url = tab === 'conversations' ? '/dani/history' : `/dani/history?tab=${tab}`;
        window.history.pushState(null, '', url);
    }, []);

    // Rename modal state
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameTarget, setRenameTarget] = useState<
        { type: 'conversation'; id: string } | { type: 'folder'; id: number } | null
    >(null);

    // Create folder modal
    const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // Share modal state
    const [shareModalConversation, setShareModalConversation] = useState<{
        id: string;
        title: string;
        reportId?: number;
    } | null>(null);

    // Delete states
    const [deleteFolderTarget, setDeleteFolderTarget] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [isDeletingFolder, setIsDeletingFolder] = useState(false);

    // Expanded folders
    const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
    const [reportExpandedFolders, setReportExpandedFolders] = useState<Set<number>>(new Set());

    // Split folders by type
    const conversationFolders = useMemo(
        () => folders.filter((f) => !f.folderType || f.folderType === 'conversation'),
        [folders],
    );
    const reportTypeFolders = useMemo(
        () => folders.filter((f) => f.folderType === 'report'),
        [folders],
    );

    // Unfiled items: conversations/reports not assigned to any folder
    const unfiledConversations = useMemo(
        () => conversations.filter((c) => !c.folderId),
        [conversations],
    );

    const unfiledReports = useMemo(() => reports.filter((r) => !r.folderId), [reports]);

    // Search — sync localSearch with the active tab's search state
    useEffect(() => {
        setLocalSearch(activeTab === 'reports' ? reportSearchTerm : conversationSearchTerm);
    }, [conversationSearchTerm, reportSearchTerm, activeTab]);

    // Auto-expand folders that have search results
    useEffect(() => {
        if (!conversationSearchTerm) return;
        const foldersWithResults = conversationFolders
            .filter((f) => (folderedConversations[f.id]?.length ?? 0) > 0)
            .map((f) => f.id);
        if (foldersWithResults.length > 0) {
            setExpandedFolders((prev) => {
                const next = new Set(prev);
                foldersWithResults.forEach((id) => next.add(id));
                return next;
            });
        }
    }, [conversationSearchTerm, folderedConversations, conversationFolders]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setLocalSearch(value);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (activeTab === 'reports') {
                debounceTimerRef.current = setTimeout(() => searchReports(value), 300);
            } else {
                debounceTimerRef.current = setTimeout(() => searchConversations(value), 300);
            }
        },
        [searchConversations, searchReports, activeTab],
    );

    const handleClearSearch = useCallback(() => {
        setLocalSearch('');
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (activeTab === 'reports') {
            searchReports('');
        } else {
            searchConversations('');
        }
    }, [searchConversations, searchReports, activeTab]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // Conversation handlers
    const handleConversationSelect = useCallback(
        (conversationId: string) => {
            if (conversationId === NEW_CONVERSATION_ID) startNewConversation();
            else openConversation(conversationId);
        },
        [startNewConversation, openConversation],
    );

    // Rename
    const openRenameConversation = useCallback(
        (conversationId: string) => {
            const conversation = conversations.find((c) => c.conversationId === conversationId);
            setRenameTarget({ type: 'conversation', id: conversationId });
            setRenameValue(conversation?.title || '');
            setIsRenameModalOpen(true);
        },
        [conversations],
    );

    const openRenameFolder = useCallback((folder: StudioAIFolder) => {
        setRenameTarget({ type: 'folder', id: folder.id });
        setRenameValue(folder.name);
        setIsRenameModalOpen(true);
    }, []);

    const handleRenameConfirm = useCallback(async () => {
        if (!renameTarget || !renameValue.trim()) return;
        try {
            if (renameTarget.type === 'conversation')
                await renameConversation(renameTarget.id, renameValue.trim());
            else await renameFolder(renameTarget.id, renameValue.trim());
        } catch {
            // Error handling in context
        }
        setIsRenameModalOpen(false);
        setRenameTarget(null);
        setRenameValue('');
    }, [renameTarget, renameValue, renameConversation, renameFolder]);

    // Folder CRUD
    const handleCreateFolder = useCallback(async () => {
        if (!newFolderName.trim()) return;
        const folderType = activeTab === 'reports' ? 'report' : 'conversation';
        try {
            const folder = await createFolder(newFolderName.trim(), folderType);
            const setExpanded =
                activeTab === 'reports' ? setReportExpandedFolders : setExpandedFolders;
            setExpanded((prev) => {
                const next = new Set(prev);
                next.add(folder.id);
                return next;
            });
        } catch {
            // Error handling in context
        }
        setIsCreateFolderModalOpen(false);
        setNewFolderName('');
    }, [newFolderName, createFolder, activeTab]);

    const handleDeleteFolder = useCallback((folder: StudioAIFolder) => {
        setDeleteFolderTarget({ id: folder.id, name: folder.name });
    }, []);

    const confirmDeleteFolder = useCallback(async () => {
        if (!deleteFolderTarget) return;
        setIsDeletingFolder(true);
        try {
            await deleteFolder(deleteFolderTarget.id);
        } catch {
            // Error handling in context
        } finally {
            setIsDeletingFolder(false);
            setDeleteFolderTarget(null);
        }
    }, [deleteFolderTarget, deleteFolder]);

    const toggleFolderExpand = useCallback((folderId: number) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
    }, []);

    const toggleReportFolderExpand = useCallback((folderId: number) => {
        setReportExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
    }, []);

    const handleReportOpen = useCallback(
        (report: StudioAIReport) => {
            // Primary: trust output_format from the API once the backend returns it.
            // Fallbacks (for reports generated before the backend ships the field):
            //   1. slide-deck-v1-{id} in localStorage — user has already opened the editor
            //   2. studio-ai.pptxReportIds — written at completion time for new reports
            const isPptx =
                report.output_format === 'pptx' ||
                Boolean(
                    typeof window !== 'undefined' &&
                    window.localStorage.getItem(`slide-deck-v1-${report.id}`),
                ) ||
                isPptxReport(report.id) ||
                isPptxConversation(report.conversationId);

            // `/dani/{conv}/{reportId}` is the plan-mode report itself
            // ([reportId]/page.tsx renders it directly); a pptx/slide-deck
            // report instead lives at `/dani/{conv}/ai/iooh?report={id}`.
            // Bare `/dani/{conv}` is just the conversation's chat.
            router.push(
                isPptx
                    ? `/dani/${report.conversationId}/ai/iooh?report=${report.id}`
                    : `/dani/${report.conversationId}/${report.id}`,
            );
        },
        [router],
    );

    const handleReportRename = useCallback(
        (conversationId: string) => {
            const report = reports.find((r) => r.conversationId === conversationId);
            setRenameTarget({ type: 'conversation', id: conversationId });
            setRenameValue(report?.title || '');
            setIsRenameModalOpen(true);
        },
        [reports],
    );

    const handleReportShare = useCallback(
        (conversationId: string) => {
            const report = reports.find((r) => r.conversationId === conversationId);
            setShareModalConversation({
                id: conversationId,
                title: report?.title || 'Report',
                reportId: report?.id,
            });
        },
        [reports],
    );

    // Share
    const handleOpenShareModal = useCallback(
        (conversationId: string) => {
            const conversation = conversations.find((c) => c.conversationId === conversationId);
            setShareModalConversation({
                id: conversationId,
                title: conversation?.title || 'Conversation',
            });
        },
        [conversations],
    );

    const formatSharedByName = (sharedBy: SharedWithMeConversation['sharedBy']) => {
        return `${sharedBy.firstName} ${sharedBy.lastName}`.trim() || sharedBy.email;
    };

    const handleSharedItemOpen = useCallback(
        (conversationId: string, reportId?: number) => {
            if (reportId) {
                router.push(`/dani/${conversationId}/${reportId}?shared=true`);
            } else {
                openConversation(conversationId);
            }
        },
        [router, openConversation],
    );

    const isLoading =
        foldersLoading ||
        (conversationsLoading && conversations.length === 0 && folders.length === 0);

    return (
        <div className={`flex h-full flex-col bg-background ${className}`}>
            {/* Header */}
            <div className="shrink-0 border-b border-divider px-6 pb-0 pt-6">
                <div className="mx-auto max-w-5xl">
                    {/* Top row */}
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-semibold tracking-tight text-foreground">
                            {STUDIO_AI_NAME} Conversation History
                        </h1>
                        <div className="flex items-center gap-2">
                            <Button
                                aria-label="New folder"
                                variant="flat"
                                size="sm"
                                hidden={activeTab !== 'conversations' && activeTab !== 'reports'}
                                onPress={() => setIsCreateFolderModalOpen(true)}
                                startContent={<Icon icon="lucide:folder-plus" width={15} />}
                                className="h-9 w-32 rounded-large text-[13px] text-default-600"
                            >
                                New folder
                            </Button>
                            <Button
                                aria-label="New chat"
                                size="sm"
                                variant="solid"
                                color="primary"
                                onPress={() => startNewConversation()}
                                startContent={<Icon icon="lucide:plus" width={14} />}
                                className="h-9 w-32 rounded-large text-[13px] font-medium"
                            >
                                New chat
                            </Button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="mt-4">
                        <Input
                            id="search-conversations"
                            size="sm"
                            placeholder={
                                activeTab === 'reports'
                                    ? 'Search reports...'
                                    : 'Search conversations...'
                            }
                            value={localSearch}
                            onValueChange={handleSearchChange}
                            className="max-w-md"
                            aria-label={
                                activeTab === 'reports' ? 'Search reports' : 'Search conversations'
                            }
                            startContent={
                                <Icon
                                    icon="lucide:search"
                                    width={16}
                                    className="text-default-400"
                                />
                            }
                            endContent={
                                localSearch ? (
                                    <Button
                                        isIconOnly
                                        variant="light"
                                        radius="full"
                                        size="sm"
                                        className="h-5 w-5 min-w-5"
                                        aria-label="Clear search"
                                        onPress={handleClearSearch}
                                    >
                                        <Icon
                                            icon="lucide:x"
                                            width={12}
                                            className="text-default-400"
                                        />
                                    </Button>
                                ) : null
                            }
                            classNames={{
                                inputWrapper:
                                    'h-9 min-h-9 bg-default-100 border-none shadow-none hover:bg-default-200 transition-colors',
                                input: 'text-small',
                            }}
                        />
                    </div>

                    {/* Tabs */}
                    <Tabs
                        selectedKey={activeTab}
                        onSelectionChange={handleTabChange}
                        variant="underlined"
                        size="sm"
                        classNames={{
                            tabList: 'gap-6 relative rounded-none p-0 mt-4',
                            tab: 'h-10 px-0',
                            cursor: 'w-full bg-primary',
                            tabContent: 'group-data-[selected=true]:text-primary font-medium',
                        }}
                    >
                        <Tab
                            key="conversations"
                            title={
                                <span className="flex items-center gap-1.5">
                                    Conversations
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        className="h-5 min-w-0 px-1.5 text-mini"
                                    >
                                        {conversations.length}
                                    </Chip>
                                </span>
                            }
                        />
                        {isPlannerEnabled && (
                            <Tab
                                key="reports"
                                title={
                                    <span className="flex items-center gap-1.5">
                                        Reports{' '}
                                        {reports.length > 0 && (
                                            <Chip
                                                size="sm"
                                                variant="flat"
                                                className="h-5 min-w-0 px-1.5 text-mini"
                                            >
                                                {reports.length}
                                            </Chip>
                                        )}
                                    </span>
                                }
                            />
                        )}
                        <Tab
                            key="feedback"
                            title={
                                <span className="flex items-center gap-1.5">
                                    Feedback
                                    {unreadFeedbackCount > 0 && (
                                        <Chip
                                            size="sm"
                                            color="danger"
                                            variant="flat"
                                            className="h-5 min-w-0 px-1.5 text-mini"
                                        >
                                            {unreadFeedbackCount}
                                        </Chip>
                                    )}
                                </span>
                            }
                        />
                        <Tab
                            key="shared"
                            title={
                                <span className="flex items-center gap-1.5">
                                    Shared with me
                                    {sharedWithMe.length > 0 && (
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            className="h-5 min-w-0 px-1.5 text-mini"
                                        >
                                            {sharedWithMe.length}
                                        </Chip>
                                    )}
                                </span>
                            }
                        />
                    </Tabs>
                </div>
            </div>

            {/* Content */}
            <ScrollShadow className="flex-1 overflow-y-auto">
                <div className="mx-auto max-w-5xl px-6 py-4">
                    {activeTab === 'conversations' ? (
                        <ConversationsTab
                            folders={conversationFolders}
                            folderedConversations={folderedConversations}
                            folderConversationTotals={folderConversationTotals}
                            folderConversationParams={folderConversationParams}
                            folderConversationFetching={folderConversationFetching}
                            onFolderPageChange={setFolderConversationPage}
                            onFolderLimitChange={setFolderConversationLimit}
                            unfiledConversations={unfiledConversations}
                            unfiledIsFetching={conversationsIsFetching}
                            expandedFolders={expandedFolders}
                            isLoading={isLoading}
                            currentConversationId={currentConversationId}
                            onToggleExpand={toggleFolderExpand}
                            onOpen={handleConversationSelect}
                            onRename={openRenameConversation}
                            onDelete={deleteConversation}
                            onShare={handleOpenShareModal}
                            onDuplicate={duplicateConversation}
                            onMoveToFolder={moveConversationToFolder}
                            onRenameFolder={openRenameFolder}
                            onDeleteFolder={handleDeleteFolder}
                            allFolders={conversationFolders}
                            page={conversationPage}
                            totalPages={totalPages}
                            totalUnfiled={totalUnfiledConversations}
                            limit={conversationLimit}
                            onPageChange={setConversationPage}
                            onLimitChange={setConversationLimit}
                            searchTerm={conversationSearchTerm}
                        />
                    ) : activeTab === 'reports' ? (
                        <ReportsTab
                            folders={reportTypeFolders}
                            folderedReports={folderedReports}
                            folderReportTotals={folderReportTotals}
                            folderReportParams={folderReportParams}
                            folderReportFetching={folderReportFetching}
                            onFolderPageChange={setFolderReportPage}
                            onFolderLimitChange={setFolderReportLimit}
                            unfiledReports={unfiledReports}
                            unfiledIsFetching={reportsIsFetching}
                            expandedFolders={reportExpandedFolders}
                            isLoading={
                                foldersLoading ||
                                (reportsLoading && reports.length === 0 && folders.length === 0)
                            }
                            onToggleExpand={toggleReportFolderExpand}
                            onOpen={handleReportOpen}
                            onRename={handleReportRename}
                            onDelete={deleteConversation}
                            onShare={handleReportShare}
                            onMoveToFolder={moveConversationToFolder}
                            onRenameFolder={openRenameFolder}
                            onDeleteFolder={handleDeleteFolder}
                            allFolders={reportTypeFolders}
                            page={reportPage}
                            totalPages={reportTotalPages}
                            totalUnfiled={totalUnfiledReports}
                            limit={reportLimit}
                            onPageChange={setReportPage}
                            onLimitChange={setReportLimit}
                        />
                    ) : activeTab === 'shared' ? (
                        <SharedTab
                            sharedWithMe={sharedWithMe}
                            isLoading={sharedWithMeLoading}
                            currentConversationId={currentConversationId}
                            onOpen={handleSharedItemOpen}
                            onDuplicate={duplicateConversation}
                            formatSharedByName={formatSharedByName}
                        />
                    ) : activeTab === 'feedback' ? (
                        <FeedbackTab
                            userFeedback={userFeedback}
                            userFeedbackLoading={userFeedbackLoading}
                            userFeedbackFilter={userFeedbackFilter}
                            setUserFeedbackFilter={setUserFeedbackFilter}
                            markFeedbackAsRead={markFeedbackAsRead}
                            refetchUserFeedback={refetchUserFeedback}
                            openConversation={openConversation}
                            unreadFeedbackCount={unreadFeedbackCount}
                        />
                    ) : null}
                </div>
            </ScrollShadow>

            {/* Modals */}
            {shareModalConversation && (
                <ShareModal
                    isOpen={!!shareModalConversation}
                    onClose={() => setShareModalConversation(null)}
                    conversationId={shareModalConversation.id}
                    conversationTitle={shareModalConversation.title}
                    organizationUsers={organizationUsers}
                    onShare={shareConversation}
                    onGetShares={getConversationShares}
                    onRevokeShare={revokeShare}
                    reportId={shareModalConversation.reportId}
                />
            )}
            <RenameModal
                isOpen={isRenameModalOpen}
                value={renameValue}
                label="New Name"
                title={renameTarget?.type === 'folder' ? 'Rename Folder' : 'Rename Conversation'}
                confirmText="Rename"
                cancelText="Cancel"
                onChange={setRenameValue}
                onClose={() => {
                    setIsRenameModalOpen(false);
                    setRenameTarget(null);
                    setRenameValue('');
                }}
                onConfirm={handleRenameConfirm}
            />
            <RenameModal
                isOpen={isCreateFolderModalOpen}
                value={newFolderName}
                label="Folder Name"
                title="Create Folder"
                confirmText="Create"
                cancelText="Cancel"
                onChange={setNewFolderName}
                onClose={() => {
                    setIsCreateFolderModalOpen(false);
                    setNewFolderName('');
                }}
                onConfirm={handleCreateFolder}
            />
            <ConfirmationModal
                isOpen={!!deleteFolderTarget}
                onOpenChange={(open) => {
                    if (!open) setDeleteFolderTarget(null);
                }}
                onConfirm={confirmDeleteFolder}
                title="Delete Folder"
                message={`Are you sure you want to delete "${deleteFolderTarget?.name}"?`}
                description="Conversations inside this folder will be moved to your unfiled conversations. They will not be deleted."
                confirmText="Delete"
                cancelText="Cancel"
                confirmColor="danger"
                icon="lucide:trash-2"
                iconColor="text-danger"
                isLoading={isDeletingFolder}
            />
        </div>
    );
}
